import { getScrollTop, scrollContainerMediaQuery } from '@theme/scroll-container';
import { RecentlyViewed } from '@theme/recently-viewed-products';

const headers = new WeakMap();
const searches = new WeakMap();
const zoomOpeners = new WeakMap();

function extendHeader(header) {
  if (headers.has(header)) return;
  const resetAtTop = () => {
    if (!header.isConnected || header.getAttribute('sticky') !== 'scroll-up' || getScrollTop() > 0) return;
    if (header.dataset.stickyState !== 'inactive') header.dataset.stickyState = 'inactive';
    if (header.dataset.scrollDirection !== 'none') header.dataset.scrollDirection = 'none';
  };
  const observer = new MutationObserver(resetAtTop);
  observer.observe(header, { attributes: true, attributeFilter: ['data-sticky-state', 'data-scroll-direction', 'sticky'] });
  headers.set(header, { observer, resetAtTop });
  resetAtTop();
}

function resetHeaders() {
  document.querySelectorAll('header-component').forEach((header) => headers.get(header)?.resetAtTop());
}

document.addEventListener('scroll', resetHeaders, { capture: true, passive: true });
window.addEventListener('pageshow', resetHeaders);
scrollContainerMediaQuery.addEventListener('change', () => requestAnimationFrame(resetHeaders));

function extendSearch(search) {
  if (searches.has(search)) return;
  if (typeof search.resetSearch !== 'function') {
    customElements.whenDefined('predictive-search-component').then(() => {
      if (search.isConnected) extendSearch(search);
    });
    return;
  }
  const input = search.querySelector('input[type="search"]');
  const results = search.querySelector('[ref="predictiveSearchResults"]');
  if (!input || !results) return;
  const modal = search.closest('dialog');
  const dialog = search.closest('dialog-component');
  let emptyStateLoaded = RecentlyViewed.getProducts().length > 0;
  const sync = () => {
    const listbox = results.querySelector('[role="listbox"]');
    const expanded = Boolean(listbox && !listbox.closest('[hidden], [inert]') && listbox.getClientRects().length > 0 && (!modal || modal.open));
    input.setAttribute('aria-expanded', String(expanded));
  };
  const observer = new MutationObserver(sync);
  observer.observe(search, { childList: true, subtree: true, attributes: true, attributeFilter: ['hidden', 'inert'] });
  if (modal) observer.observe(modal, { attributes: true, attributeFilter: ['open', 'hidden', 'inert'] });
  const resize = new ResizeObserver(sync);
  resize.observe(results);
  const onOpen = () => {
    sync();
    if (!emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      emptyStateLoaded = true;
      search.resetSearch(false);
    }
  };
  dialog?.addEventListener('dialog:open', onOpen);
  dialog?.addEventListener('dialog:close', sync);
  searches.set(search, { observer, resize, dialog, sync, onOpen });
  sync();
}

function canFocus(element) {
  return element instanceof HTMLElement && element.isConnected && !element.closest('[aria-hidden="true"], [hidden], [inert]') && element.getClientRects().length > 0;
}

document.addEventListener('click', (event) => {
  if (!(event.target instanceof Element)) return;
  const trigger = event.composedPath().find((node) => node instanceof Element &&
    (node.classList.contains('product-media-container__zoom-button') || /#zoom-dialog-[^/]+\/open\//.test(node.getAttribute('on:click') ?? '')));
  const zoom = trigger?.closest('media-gallery')?.querySelector('zoom-dialog');
  if (zoom) zoomOpeners.set(zoom, event.target.closest('button'));
}, { capture: true });

window.addEventListener('dialog:close', () => {
  document.querySelectorAll('zoom-dialog').forEach((zoom) => {
    if (!zoomOpeners.has(zoom) || zoom.querySelector('dialog')?.open) return;
    const opener = zoomOpeners.get(zoom);
    const fallback = Array.from(zoom.closest('media-gallery')?.querySelectorAll('.product-media-container__zoom-button') ?? []).find(canFocus);
    const target = canFocus(opener) ? opener : fallback;
    zoomOpeners.delete(zoom);
    if (target instanceof HTMLElement) target.focus({ preventScroll: true });
  });
});

function scan(root) {
  if (!(root instanceof Element || root instanceof Document)) return;
  if (root.matches?.('header-component')) extendHeader(root);
  if (root.matches?.('predictive-search-component')) extendSearch(root);
  root.querySelectorAll('header-component').forEach(extendHeader);
  root.querySelectorAll('predictive-search-component').forEach(extendSearch);
}

const lifecycle = new MutationObserver((records) => {
  for (const record of records) {
    for (const node of record.addedNodes) scan(node);
    for (const node of record.removedNodes) {
      if (!(node instanceof Element) || node.isConnected) continue;
      for (const header of [node, ...node.querySelectorAll('header-component')]) {
        headers.get(header)?.observer.disconnect();
        headers.delete(header);
      }
      for (const search of [node, ...node.querySelectorAll('predictive-search-component')]) {
        const state = searches.get(search);
        if (!state) continue;
        state.observer.disconnect();
        state.resize.disconnect();
        state.dialog?.removeEventListener('dialog:open', state.onOpen);
        state.dialog?.removeEventListener('dialog:close', state.sync);
        searches.delete(search);
      }
    }
  }
});
lifecycle.observe(document.documentElement, { childList: true, subtree: true });
scan(document);
